import { useMemo, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TOOLBAR = [
  [{ header: [1, 2, 3, 4, false] }],
  [{ font: [] }],
  [{ size: ['small', false, 'large', 'huge'] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  [{ align: [] }],
  ['link', 'image'],
  ['blockquote', 'code-block'],
  ['clean'],
];

const formats = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'list', 'indent',
  'align',
  'link', 'image',
  'blockquote', 'code-block',
];

async function uploadImage(file) {
  const token = localStorage.getItem('token');
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${BACKEND_URL}/api/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Yükleme başarısız');
  }
  const data = await res.json();
  // Build absolute URL so image shows in preview
  return data.url.startsWith('http') ? data.url : `${BACKEND_URL}${data.url}`;
}

export function RichTextEditor({ value, onChange, placeholder = 'İçeriği buraya yazın...', minHeight = 200 }) {
  const quillRef = useRef(null);

  const modules = useMemo(() => ({
    toolbar: {
      container: TOOLBAR,
      handlers: {
        image: function () {
          const input = document.createElement('input');
          input.setAttribute('type', 'file');
          input.setAttribute('accept', 'image/*');
          input.click();
          input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return;
            if (file.size > 10 * 1024 * 1024) {
              toast.error('Dosya çok büyük. Maksimum 10 MB.');
              return;
            }
            const editor = quillRef.current?.getEditor();
            const range = editor?.getSelection(true);
            try {
              toast.loading('Resim yükleniyor...', { id: 'quill-upload' });
              const url = await uploadImage(file);
              if (editor && range) {
                editor.insertEmbed(range.index, 'image', url, 'user');
                editor.setSelection(range.index + 1);
              }
              toast.success('Resim yüklendi', { id: 'quill-upload' });
            } catch (err) {
              toast.error(err.message || 'Resim yüklenemedi', { id: 'quill-upload' });
            }
          };
        },
      },
    },
  }), []);

  return (
    <div className="rich-editor-wrapper">
      <style>{`
        .rich-editor-wrapper .ql-container {
          min-height: ${minHeight}px;
          font-size: 14px;
          border-bottom-left-radius: 8px;
          border-bottom-right-radius: 8px;
          border-color: #e5e7eb;
        }
        .rich-editor-wrapper .ql-toolbar {
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
          border-color: #e5e7eb;
          background: #fafafa;
        }
        .rich-editor-wrapper .ql-editor {
          min-height: ${minHeight}px;
        }
        .rich-editor-wrapper .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
        }
      `}</style>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ''}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
    </div>
  );
}

// Render HTML content safely
export function HtmlContent({ html, className = '' }) {
  if (!html) return null;
  const isHtml = html.includes('<') && html.includes('>');
  if (!isHtml) {
    // Plain text: wrap each non-empty line in <p> for nice paragraph formatting
    const paragraphs = html.split(/\n+/).map(s => s.trim()).filter(Boolean);
    return (
      <>
        <style>{`
          .rich-html-content { word-wrap: break-word; overflow-wrap: break-word; }
          .rich-html-content p { margin: 0.6em 0; line-height: 1.65; }
          .rich-html-content p:first-child { margin-top: 0; }
          .rich-html-content p:last-child { margin-bottom: 0; }
        `}</style>
        <div className={`rich-html-content ${className}`}>
          {paragraphs.length > 0
            ? paragraphs.map((p, i) => <p key={i}>{p}</p>)
            : <p>{html}</p>}
        </div>
      </>
    );
  }
  // Replace non-breaking spaces (and Unicode NBSP) with regular spaces
  const cleanedHtml = html.replace(/&nbsp;/g, ' ').replace(/\u00a0/g, ' ');
  return (
    <>
      <style>{`
        .rich-html-content { word-wrap: break-word; overflow-wrap: break-word; word-break: break-word; }
        .rich-html-content p { margin: 0.6em 0; line-height: 1.65; }
        .rich-html-content p:first-child { margin-top: 0; }
        .rich-html-content p:last-child { margin-bottom: 0; }
        .rich-html-content h1, .rich-html-content h2, .rich-html-content h3,
        .rich-html-content h4 { font-family: Merriweather, serif; font-weight: 700; color: #78350f; margin: 1.2em 0 0.6em; line-height: 1.2; }
        .rich-html-content h1 { font-size: 1.875em; }
        .rich-html-content h2 { font-size: 1.5em; }
        .rich-html-content h3 { font-size: 1.25em; }
        .rich-html-content h4 { font-size: 1.1em; }
        .rich-html-content ul, .rich-html-content ol { padding-left: 1.5em; margin: 0.6em 0; }
        .rich-html-content ul { list-style: disc; }
        .rich-html-content ol { list-style: decimal; }
        .rich-html-content li { margin: 0.25em 0; }
        .rich-html-content blockquote { border-left: 3px solid #f59e0b; padding-left: 1em; margin: 1em 0; font-style: italic; color: #92400e; }
        .rich-html-content a { color: #ea580c; text-decoration: underline; }
        .rich-html-content a:hover { color: #c2410c; }
        .rich-html-content img { max-width: 100%; height: auto; border-radius: 8px; margin: 0.75em 0; }
        .rich-html-content strong, .rich-html-content b { font-weight: 700; color: #78350f; }
        .rich-html-content em, .rich-html-content i { font-style: italic; }
        .rich-html-content u { text-decoration: underline; }
        .rich-html-content code { background: #fef3c7; color: #78350f; padding: 0.15em 0.4em; border-radius: 4px; font-family: ui-monospace, monospace; font-size: 0.9em; }
        .rich-html-content pre { background: #fef3c7; color: #78350f; padding: 0.85em 1em; border-radius: 8px; overflow-x: auto; margin: 0.85em 0; }
        .rich-html-content .ql-align-center { text-align: center; }
        .rich-html-content .ql-align-right { text-align: right; }
        .rich-html-content .ql-align-justify { text-align: justify; }
        .rich-html-content .ql-indent-1 { padding-left: 1.5em; }
        .rich-html-content .ql-indent-2 { padding-left: 3em; }
        .rich-html-content .ql-indent-3 { padding-left: 4.5em; }
      `}</style>
      <div
        className={`rich-html-content max-w-none ${className}`}
        dangerouslySetInnerHTML={{ __html: cleanedHtml }}
      />
    </>
  );
}
